'use strict';

import '@auth0/auth0-spa-js';
import 'angular-auth0-spa-js';
import 'angular-translate';
import 'angular-translate-loader-static-files';
import { environment } from '@quartz-iam-legacy/environments/environment';
import { AnalyticsUtils } from '@quartz-iam-legacy/app/analytics/analyticsUtils';
import { MonitoringUtils } from '@quartz-iam-legacy/app/monitoring/monitoringUtils';

var appMain = angular.module('app.main', [
    'ngAnimate',
    'ngSanitize',
    'auth0.auth0',
    'modelOptions',
    'angularMoment',
    'ui.router',
    'ui.ace',
    'ui.select',
    'angular-growl',
    'angular.filter',
    'angularFileUpload',
    'ngclipboard',
    'btford.markdown',
    'xeditable',
    'hljs',
    'pascalprecht.translate',
    'filters.dateUTC',
    'filters.filterByMultiTokens',
    'filters.partition',
    'services.auth',
    'services.authenticatedUser',
    'services.featureService',
    'services.language',
    'languageControllers',
    'directives.card',
    'directives.confirmClickDirective',
    'directives.employees',
    'directives.empStaging',
    'directives.fadeAnim',
    'directives.fallback',
    'directives.focus',
    'directives.IAMContact',
    'directives.iamEvent',
    'directives.icheck',
    'directives.inputEnter',
    'directives.placeholder',
    'directives.redirectNewIam',
    'directives.systemsOwnedDecommissionPermissions',
    'directives.systemsOwnershipGroups',
    'directives.userRequests',
    'directives.userRequestFollowups',
    'directives.selfAccessRequestDecision',
    'directives.scrollArrowIndicator',
    'directives.businessUnit',
    'directives.businessUnitMember',
    'helpers.array',
    'topBarControllers',
    'iamAdminControllers',
    'EmployeeEventControllers',
    'appMain.Config',

    'adminRoutes',
    'adminDefaultEntitlementRoutes',
    'adminEmailTemplatesRoutes',
    'adminBusinessUnitsRoutes',
    'adminSupervisorReviewRoutes',
    'adminSystemOwnerReviewRoutes',
    'employeeRoutes',
    'homeRoutes',
    'HRRoutes',
    'itPurchasingRoutes',
    'staticHtmlPageRoutes',
    'supervisorAccessReviewRoutes',
    'supervisorAuthorizingUserRequestRoutes',
    'systemOwnerReviewRoutes',
    'selfAccessRequestArrivalRoutes',
    'selfAccessRequestAdHocRoutes',
    'systemOwnerUserRequestRoutes',
    'userRequestRoutes',
    'userRequestArrivalRoutes',
    'userRequestEditRoutes',
    'userRequestProcessRoutes',
]);

appMain.config(['$provide', '$locationProvider', function ($provide, $locationProvider) {
  // Remove the ! from the hash so that
  // auth0.js can properly parse it
  $locationProvider.hashPrefix('');

  $locationProvider.html5Mode(true);
}]);

// Configure angular-translate
appMain.config(['$translateProvider', function ($translateProvider) {
  // Use static files loader to load translations from JSON files
  $translateProvider.useStaticFilesLoader({
    prefix: '/assets/i18n/',
    suffix: '.json'
  });

  // Set fallback language (if translation key not found, fall back to English)
  $translateProvider.fallbackLanguage('en');

  // Enable HTML sanitization (allows safe HTML tags like <br/>, <strong> in translations)
  $translateProvider.useSanitizeValueStrategy('sanitizeParameters');
}]);

appMain.controller('AppCtrlMain', function () {
    // Main app controller
});

// Forward AngularJS uncaught exceptions to Sentry. The library's window-level handler
// catches synchronous errors, but AngularJS swallows controller/digest errors via its
// own $exceptionHandler — without this decorator they never reach Sentry.
appMain.factory('$exceptionHandler', ['$log', function ($log) {
  return function (exception, cause) {
    $log.error.apply($log, arguments);
    MonitoringUtils.captureException(exception);
  };
}]);

appMain.config(
  ['$injector', '$httpProvider', 'angularAuth0Provider',
  function ($injector, $httpProvider, angularAuth0Provider) {
    // Detect authentication provider from environment
    var authProvider = environment.authProvider || 'auth0';

    // Conditionally initialize Auth0 if using Auth0 authentication
    if (authProvider === 'auth0') {
      // Initialization for the angular-auth0 library
      angularAuth0Provider.init({
        audience: environment.auth.audience,
        cacheLocation: 'localstorage',
        client_id: environment.auth.clientID,
        connection: environment.auth.connection,
        domain: environment.auth.domain,
        redirect_uri: environment.auth.redirect || window.location.origin,
        useRefreshTokens: true,
        httpInterceptor: {
          allowedList: [
            {
              uri: `${environment.apiUrl}/*`,
            },
          ],
        },
      });
    }
}]);

appMain.factory('httpRequestInterceptor', ['angularAuth0', function (angularAuth0) {
  return {
    request: function (config) {
      return angularAuth0.getTokenSilently()
        .then(function(accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;

          return config;
        });
    }
  };
}]);

appMain.config(['$httpProvider', function ($httpProvider) {
  // Detect authentication provider from environment
  var authProvider = environment.authProvider || 'auth0';

  // Conditionally register the appropriate HTTP interceptor
  if (authProvider === 'auth0') {
    $httpProvider.interceptors.push('httpRequestInterceptor');
  } else if (authProvider === 'keycloak') {
    $httpProvider.interceptors.push('keycloakInterceptor');
  }
}]);

appMain.factory('SmartCacheInterceptor', ['$q', 'mainVersion', function ($q, mainVersion) {
    return {
        request: function (config) {
            if (config.url.indexOf(".tpl.html") > -1) {
                var separator = config.url.indexOf("?") === -1 ? "?" : "&";
                config.url = config.url + separator + "v=" + mainVersion;
            }
            return config || $q.when(config);
        }
    };
}]);

appMain.config(['growlProvider', '$httpProvider', function (growlProvider, $httpProvider) {
    growlProvider.globalEnableHtml(true);

    var serverMessageInterceptor = ['$q', 'growl', function ($q, growl) {
        var _messagesKey = "GrowlMessages";
        function checkResponse(response) {
            if (response && response.data[_messagesKey] && response.data[_messagesKey].length > 0) {
                growl.addServerMessages(response.data[_messagesKey]);
            }
        }
        function success(response) {
            checkResponse(response);
            return response;
        }
        function error(response) {
            if (response.config && response.config.timeout) { return $q.reject(response); }

            checkResponse(response);
            return $q.reject(response);
        }
        return function (promise) {
            return promise.then(success, error);
        };
    }];

    var errorInterceptor = ['$q', '$injector', 'growl', function ($q, $injector, growl) {
        return {
            'responseError': function (rejection) {
                if (rejection.config && rejection.config.timeout && !rejection.config.status) { return $q.reject(rejection); }

                // Lazy-load $translate to avoid circular dependency
                // ($translate depends on $http, and $http depends on this interceptor)
                var $translate = $injector.get('$translate');

                var userMessage = rejection.data?.Message || rejection.statusText;

                // Load translations asynchronously to avoid race condition
                $q.all({
                    technicalErrorTitle: $translate('iam.app.errors.technicalErrorTitle'),
                    on: $translate('iam.app.errors.on'),
                    withMessage: $translate('iam.app.errors.withMessage')
                }).then(function(translations) {
                    var message = '';
                    if (userMessage) {
                        message = '<br/>' + translations.withMessage + ' <strong>' + userMessage + '</strong>';
                    }

                    growl.addErrorMessage('<span class="gritter-title">' + translations.technicalErrorTitle + '</span>' +
                                          '<span>' +
                                             translations.on + ' <strong>' + rejection.config?.url + '</strong></span>' +
                                             message +
                                          '</span>',
                                          { enableHtml: true });
                });

                return $q.reject(rejection);
            }
        }
    }];

    $httpProvider.responseInterceptors.push(serverMessageInterceptor);
    $httpProvider.interceptors.push(errorInterceptor);
    // $httpProvider.interceptors.push('SmartCacheInterceptor');
}]);

appMain.config(['markdownConverterProvider', function (markdownConverterProvider) {
    // options to be passed to Showdown
    // see: https://github.com/showdownjs/showdown#extensions
    markdownConverterProvider.config({
        extensions: ['psptmpl']
    });
}]);

appMain.config(['$httpProvider', function ($httpProvider) {
    // Initialize get if not there
    if (!$httpProvider.defaults.headers.get) {
        $httpProvider.defaults.headers.get = {};
    }

    // Enables Request.IsAjaxRequest() in ASP.NET MVC
    $httpProvider.defaults.headers.common["X-Requested-With"] = 'XMLHttpRequest';

    // Disable IE ajax request caching
    $httpProvider.defaults.headers.get['If-Modified-Since'] = 'Mon, 26 Jul 1997 05:00:00 GMT';
}]);

// angular.module('appMain.Config', []).constant('mainVersion', '@templateVersion');
angular.module('appMain.Config', []).constant('mainVersion', '1');

appMain.run(['$rootScope', '$window', 'editableOptions', 'authService', 'languageService', function ($rootScope, $window, editableOptions, authService, languageService) {
  editableOptions.theme = 'bs3';

  // Initialize language BEFORE authentication
  // This ensures the UI is in the correct language when authentication happens
  // Language is determined by: 1) localStorage preference, 2) browser language (if supported), 3) default 'en'
  languageService.initializeLanguage();

  // Fix OAuth callback/error URL mangled by AngularJS html5Mode
  // When Keycloak redirects with /#state=...&code=... (success) or /#error=...&state=... (error),
  // AngularJS routing treats the hash as a route path and rewrites it to /state=...&code=... or /error=...&state=...
  // We detect this and reconstruct the proper hash-based URL
  var pathname = $window.location.pathname;
  var hasOAuthCallback = pathname.indexOf('state=') !== -1 && pathname.indexOf('code=') !== -1;
  var hasOAuthError = pathname.indexOf('error=') !== -1 && pathname.indexOf('state=') !== -1;

  if (hasOAuthCallback || hasOAuthError) {
    // Extract OAuth params from path (remove leading /)
    var oauthParams = pathname.substring(1);

    // Reconstruct proper hash-based callback URL
    var fixedUrl = $window.location.origin + '/#' + oauthParams;

    // Replace URL with correct hash format
    $window.history.replaceState({}, '', fixedUrl);
  }

  // Detect Keycloak error responses
  // When check-sso finds no session, Keycloak returns #error=login_required
  // We detect this and force login redirect
  var hash = $window.location.hash;
  var forceLogin = false;

  if (hash && hash.indexOf('error=login_required') !== -1) {
    forceLogin = true;

    // Clean the error hash
    $window.history.replaceState({}, '', $window.location.pathname + $window.location.search);
  }

  // Handle post-logout redirect
  // After logout, Keycloak redirects back with ?logged_out=true
  // We detect this and clean the URL, then init will force login
  var urlParams = new URLSearchParams($window.location.search);
  var postLogout = urlParams.has('logged_out');

  if (postLogout) {
    // Clean URL by removing the logged_out parameter
    urlParams.delete('logged_out');
    var cleanUrl = $window.location.pathname;
    if (urlParams.toString()) {
      cleanUrl += '?' + urlParams.toString();
    }

    // Replace URL to clean it (no history entry)
    $window.history.replaceState({}, '', cleanUrl);

    // Force login after logout
    forceLogin = true;
  }

  // Initialize the authentication provider (Auth0 or Keycloak)
  // Pass forceLogin flag to force login redirect if:
  // - Returning from logout (postLogout)
  // - Keycloak returned error=login_required (no session)
  authService.init({ forceLogin: forceLogin })
    .then(function() {
      // Handle OAuth callback (Auth0) or check session (Keycloak)
      return authService.handleAuthentication();
    })
    .then(function(targetRoute) {
      // Check if user is authenticated
      return authService.isAuthenticated()
        .then(function(isAuthenticated) {
          if (!isAuthenticated) {
            // Not authenticated - redirect to login
            var currentRoute = $window.location.pathname + $window.location.search;
            authService.login(targetRoute || currentRoute);
          } else {
            // Authenticated - navigate to target route if provided
            if (targetRoute) {
              $window.location.href = targetRoute;
            }
          }
        });
    })
    .catch(function() {
      // keycloakProvider's init catch already logged structured details + state snapshot.
      // Re-logging `error` here would just print `undefined` in the same failure modes,
      // which is actively misleading when triaging user reports.
      console.warn('[app.run] Auth initialization chain failed — attempting login redirect');

      // On error, try to redirect to login as fallback
      var currentRoute = $window.location.pathname + $window.location.search;
      authService.login(currentRoute);
    });
}]);

appMain.run(['$rootScope', '$timeout', function ($rootScope, $timeout) {
  $rootScope.$on('keycloak:userProfileLoaded', function (event, profile) {
    AnalyticsUtils.identify(profile);
  });

  $rootScope.$on('keycloak:logout', function () {
    AnalyticsUtils.reset();
  });

  $rootScope.$on('$stateChangeSuccess', function () {
    // ui-router updates the URL after this event, so the pageview waits for the end of the digest.
    $timeout(AnalyticsUtils.capturePageview, 0, false);
  });
}]);
